import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Modal,
  Form,
  Spinner,
  Card,
  Table,
  Row,
  Col,
} from "react-bootstrap";
import Layout from "../../layout/Layout";
import { uploadIadeRapor } from "../../../api/kalite/iadeRaporApi";
import {
  Chart,
  ArcElement,
  Tooltip,
  Legend,
  DoughnutController,
  CategoryScale,
  LinearScale,
} from "chart.js";

Chart.register(
  ArcElement,
  Tooltip,
  Legend,
  DoughnutController,
  CategoryScale,
  LinearScale
);


function IadeRapor() {
  const [form, setForm] = useState({
    file: null,
    startDate: "",
    endDate: "",
    cari: "",
    kullaniciToplamTonaj: "",
    totalYearsTips: "",
    recordsTopN: 5,
    recordTip: "",
  });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({
    show: false,
    type: "success",
    message: "",
  });
  const [data, setData] = useState(null);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm((prev) => ({ ...prev, [name]: files ? files[0] : value }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file)
      return setFeedback({
        show: true,
        type: "error",
        message: "Lütfen bir Excel dosyası seçin.",
      });

    try {
      setLoading(true);
      const res = await uploadIadeRapor(form);
      setData(res.data.reports);
      setFeedback({
        show: true,
        type: "success",
        message: "Analiz tamamlandı ",
      });
    } catch (err) {
      console.error(err);
      setFeedback({
        show: true,
        type: "error",
        message: err.message || "Yükleme sırasında hata oluştu ❌",
      });
    } finally {
      setLoading(false);
    }
  };

  //  Format helpers
  const fmt = (v) => new Intl.NumberFormat("tr-TR").format(v || 0);
  const pct = (v) => (v * 100).toFixed(2).replace(".", ",") + " %";

  //  Pie chart ref
  const pieRef = useRef(null);
  useEffect(() => {
    if (!data?.iadeMini?.kpi || !pieRef.current) return;
    const oran = data.iadeMini.kpi.oran ?? null;
    if (typeof oran !== "number") return;
    const ctx = pieRef.current.getContext("2d");

    const chart = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: ["İade (%)", "Kalan (%)"],
        datasets: [
          {
            data: [oran * 100, 100 - oran * 100],
            backgroundColor: ["#dc3545", "#198754"],
          },
        ],
      },
      options: {
        plugins: { legend: { position: "bottom" } },
        maintainAspectRatio: false,
      },
    });
    return () => chart.destroy();
  }, [data]);

  return (
    <Layout>
      <h2 className="fw-bold mb-4">İade Raporu Analiz</h2>

      {/* 📂 Upload */}
      <Card className="p-4 shadow-sm border-0 mb-4" style={{ maxWidth: 600 }}>
        <Form onSubmit={handleUpload}>
          <Form.Group className="mb-3">
            <Form.Label>Excel Dosyası (.xlsx / .xls)</Form.Label>
            <Form.Control
              type="file"
              name="file"
              accept=".xlsx,.xls"
              onChange={handleChange}
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Label>Başlangıç Tarihi</Form.Label>
              <Form.Control
                type="date"
                name="startDate"
                value={form.startDate}
                onChange={handleChange}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Bitiş Tarihi</Form.Label>
              <Form.Control
                type="date"
                name="endDate"
                value={form.endDate}
                onChange={handleChange}
              />
            </Col>
          </Row>

          <div className="text-end mt-3">
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Yükle ve Analiz Et"
              )}
            </Button>
          </div>
        </Form>
      </Card>

      {/* 📊 iadeMini render */}
      {data?.iadeMini && (
        <Card className="p-3 mb-4">
          <h5 className="mb-3">İade Genel</h5>

          {/* KPI */}
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-4 mb-4">
            <div className="d-flex flex-wrap gap-4 align-items-center">
              <div>
                <div className="text-muted">İade Oranı</div>
                <div className="fs-3 fw-bold">
                  {pct(data.iadeMini.kpi.oran)}
                </div>
              </div>
              <div>
                <div className="text-muted">Toplam İade (Kg)</div>
                <div className="fs-5">
                  {fmt(data.iadeMini.kpi.toplamIadeKg)}
                </div>
              </div>
              <div>
                <div className="text-muted">Üretilen Toplam (Kg)</div>
                <div className="fs-5">{fmt(data.iadeMini.kpi.girilenKg)}</div>
              </div>
            </div>

            {/* Toplam Üretim input */}
            <div className="d-flex align-items-end gap-2">
              <Form.Group>
                <Form.Label className="form-label m-0 me-2">
                  Toplam Üretim (Kg)
                </Form.Label>
                <Form.Control
                  type="number"
                  name="kullaniciToplamTonaj"
                  placeholder="700000"
                  value={form.kullaniciToplamTonaj}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      kullaniciToplamTonaj: e.target.value,
                    }))
                  }
                  style={{ maxWidth: 200 }}
                />
              </Form.Group>
              <Button
                size="sm"
                variant="primary"
                disabled={loading}
                onClick={handleUpload}
              >
                {loading ? "..." : "Uygula"}
              </Button>
            </div>
          </div>

          {/* Pie chart */}
          <div style={{ height: 200 }}>
            <canvas ref={pieRef}></canvas>
          </div>

          {/* Tip tablosu */}
          <Row className="mt-4">
            <Col md={6}>
              <h6>Tip’e Göre İade</h6>
              <Table striped hover size="sm">
                <thead>
                  <tr>
                    <th>Grup</th>
                    <th className="text-end">Kg</th>
                    <th className="text-end">%</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.iadeMini.byTip || {}).map(([k, v]) => (
                    <tr key={k}>
                      <td>{k}</td>
                      <td className="text-end">{fmt(v.kg)}</td>
                      <td className="text-end">
                        {(
                          (v.kg / data.iadeMini.kpi.toplamIadeKg) *
                          100
                        ).toFixed(2)}{" "}
                        %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>

            <Col md={6}>
              <h6>Malzeme’ye Göre İade</h6>
              <Table striped hover size="sm">
                <thead>
                  <tr>
                    <th>Malzeme</th>
                    <th className="text-end">Kg</th>
                    <th className="text-end">%</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.iadeMini.byMalzeme || {}).map(
                    ([k, v]) => (
                      <tr key={k}>
                        <td>{k}</td>
                        <td className="text-end">{fmt(v.kg)}</td>
                        <td className="text-end">
                          {(
                            (v.kg / data.iadeMini.kpi.toplamIadeKg) *
                            100
                          ).toFixed(2)}{" "}
                          %
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>
        </Card>
      )}

      {/* Feedback */}
      <Modal
        show={feedback.show}
        onHide={() => setFeedback({ ...feedback, show: false })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {feedback.type === "success" ? "Başarılı" : "Hata"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{feedback.message}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setFeedback({ ...feedback, show: false })}
          >
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}
export default IadeRapor;
